import Input from "components/input/input.component";
import Select from "components/select/select.component";
import { TwoColumnsInput } from "global";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { updateSecuritiesItems } from "redux/form/form.reducer";
import {
  selectFormErrors,
  selectSecuritiesItems,
} from "redux/form/form.selector";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import {
  DisclaimerText,
  RemoveSecurityButton,
  SecurityItemHeader,
} from "./gos-securities-step.styles";

const securityTypeOptions = [
  { value: "", name: "" },
  { value: "Stocks", name: "Stocks" },
  { value: "Bonds", name: "Bonds" },
  { value: "ETFs", name: "ETFs" },
  { value: "Mutual Fund", name: "Mutual Fund" },
  { value: "Other", name: "Other" },
];

interface Props {
  index: number;
}

const SecurityItemForm: FC<Props> = ({ index }) => {
  const dispatch = useAppDispatch();
  const securitiesItems = useAppSelector(selectSecuritiesItems);
  const formErrors = useAppSelector(selectFormErrors);
  const { t } = useTranslation();

  const item = securitiesItems[index];
  const errors = formErrors.securitiesItems?.[index] || {};
  const showHeader = securitiesItems.length > 1;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    if (
      ["approximateDollarValue", "numberOfUnits"].includes(name) &&
      value &&
      (isNaN(+value) || +value <= 0)
    )
      return;

    const updatedItems = securitiesItems.map((item, i) =>
      i === index ? { ...item, [name]: value } : item,
    );
    dispatch(updateSecuritiesItems(updatedItems));
  };

  const handleSelectChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.currentTarget;
    const updatedItems = securitiesItems.map((item, i) =>
      i === index ? { ...item, [name]: value } : item,
    );
    dispatch(updateSecuritiesItems(updatedItems));
  };

  const handleRemove = () => {
    const updatedItems = securitiesItems.filter((_, i) => i !== index);
    dispatch(updateSecuritiesItems(updatedItems));
  };

  return (
    <div>
      {showHeader && (
        <SecurityItemHeader>
          <span>
            {t("Security")} {index + 1}
          </span>
          <RemoveSecurityButton onClick={handleRemove}>
            {t("Remove")}
          </RemoveSecurityButton>
        </SecurityItemHeader>
      )}

      <Input
        label="Security name"
        type="text"
        name="securityName"
        id={`securityName-${index}`}
        onChange={handleInputChange}
        value={item.securityName}
        isError={errors?.securityName}
        maxLength={50}
        isRequired
      />

      <TwoColumnsInput isMobileFullWidth>
        <Select
          name="securityType"
          id={`securityType-${index}`}
          onChange={handleSelectChange}
          label="Security type"
          value={item.securityType}
          optionsData={securityTypeOptions}
          isError={errors?.securityType}
          withTranslation
          withValueTranslation
        />

        <Input
          label="CUSIP / symbol"
          type="text"
          name="cusipOrSymbol"
          id={`cusipOrSymbol-${index}`}
          onChange={handleInputChange}
          value={item.cusipOrSymbol}
          isError={errors?.cusipOrSymbol}
          maxLength={50}
        />
      </TwoColumnsInput>

      <TwoColumnsInput isMobileFullWidth>
        <Input
          label="# of units / shares"
          type="text"
          name="numberOfUnits"
          id={`numberOfUnits-${index}`}
          onChange={handleInputChange}
          value={item.numberOfUnits}
          isError={errors?.numberOfUnits}
          maxLength={7}
          isRequired
        />

        <Input
          label="Approximate dollar value"
          type="text"
          name="approximateDollarValue"
          id={`approximateDollarValue-${index}`}
          onChange={handleInputChange}
          value={item.approximateDollarValue}
          isError={errors?.approximateDollarValue}
          maxLength={7}
          isRequired
        />
      </TwoColumnsInput>

      <DisclaimerText>
        {t("At least one field must be provided (# or value)")}
      </DisclaimerText>
    </div>
  );
};

export default SecurityItemForm;
